// Basketball — Signals (tabbed). Ported from the CLAUDE DESIGN prototype
// (bb-overview.jsx → SignalsScreen + DailyCardTab + PropPulseTab +
// OvertimeTrapTab). Three tabs wired to real hooks:
//   • Daily Card    → useBasketballDailyCard()  (5-slot card + Hardwood Chain)
//   • Prop Pulse    → usePropPulse()
//   • Overtime Trap → useOvertimeTrap()
// HONEST: each tab renders the AwaitPanel dormant state when its hook is
// null/empty; loading → <Loading/>; error → error pill + dormant beneath.

import { useState } from "react";
import { AwaitPanel, BbHead, Loading, OtArc, fmtOdds, fmtPct, fmtLine } from "./_bbkit.tsx";
import { useBasketballDailyCard, type CardSlot, type DailyCard } from "./useBasketballDailyCard.ts";
import { usePropPulse, useOvertimeTrap } from "./useBasketballProducts.ts";

const SLOT_ORDER: CardSlot[] = ["spread", "total", "q1", "first_half", "player_prop"];
const SLOT_LABEL: Record<CardSlot, string> = {
  spread: "SPREAD", total: "TOTAL", q1: "Q1", first_half: "FIRST HALF", player_prop: "PLAYER PROP",
};

// ── Daily Card ──────────────────────────────────────────────────
function DailyCardTab(): JSX.Element {
  const { loading, error, card } = useBasketballDailyCard();
  if (loading) return <Loading label="Loading card…" />;
  const populated = !!card && (card.filled > 0 || !card.hardwoodRestDay || card.ladderPool.length > 0);

  if (!populated) {
    return (
      <>
        {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load the card" body={error} ghosts={4} /> : null}
        <AwaitPanel
          title="No qualified picks yet"
          body="Five-market daily card stays blank when the engine cannot beat its own threshold. Slots fill as the calibration sample matures."
          icon="layers"
        />
      </>
    );
  }

  const c = card as DailyCard;
  return (
    <>
      <div className="bb-section-title">
        <h3>Daily Card</h3>
        <div className="sub">5-market · {c.filled} filled · pool of {c.considered}</div>
      </div>
      <div className="bb-slots">
        {SLOT_ORDER.map((s) => {
          const p = c.slots[s] ?? null;
          if (!p) {
            return (
              <div key={s} className="bb-slot open">
                <div className="market">{SLOT_LABEL[s]}</div>
                <div className="placeholder">slot open · no qualified pick</div>
              </div>
            );
          }
          return (
            <div key={s} className="bb-slot">
              <div className="market">{p.market_code}</div>
              <div className="selection">{p.selection}</div>
              <div className="line">{p.band || SLOT_LABEL[s]}</div>
              <div className="foot">
                <div className="odds">{fmtPct(p.value_pct)}</div>
                <div className="edge">{p.confidence ? p.confidence.toLowerCase() : "—"}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={`bb-chain ${c.hardwoodRestDay ? "rest" : ""}`}>
        <div>
          <div className="k">HARDWOOD CHAIN · single play</div>
          <div className="selection">{c.hardwoodRestDay || !c.hardwoodPick ? "REST DAY" : c.hardwoodPick.selection}</div>
          <div className="sub">{c.hardwoodRestDay || !c.hardwoodPick ? "No HIGH-confidence pick today — the chain rests." : c.hardwoodPick.market_code}</div>
        </div>
        <div className="odds-big">
          {c.hardwoodPick ? fmtPct(c.hardwoodPick.value_pct) : "—"}
          <span className="u">edge</span>
        </div>
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-quaternary)", marginTop: 18, textAlign: "right" }}>
        Ladder pool · {c.considered} candidates considered
      </div>
    </>
  );
}

// ── Prop Pulse ──────────────────────────────────────────────────
function PropPulseTab(): JSX.Element {
  const { loading, error, data } = usePropPulse();
  if (loading) return <Loading label="Loading prop signals…" />;
  const populated = !!data && data.count > 0;

  if (!populated) {
    return (
      <>
        {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load prop signals" body={error} ghosts={4} /> : null}
        <AwaitPanel
          title="No prop signals yet"
          body="Player prop signals fire when usage, defence, and pace converge against a stale book line. Edges go stale fast — we only publish when the gap is real."
          icon="bolt"
        />
      </>
    );
  }
  return (
    <>
      <div className="bb-section-title">
        <h3>Prop Pulse</h3>
        <div className="sub">{data!.count} live signals · stale clocks rolling</div>
      </div>
      <div className="bb-card" style={{ padding: 0 }}>
        <div className="bb-prop-list">
          {data!.signals.map((p, i) => (
            <div key={`${p.game_id}-${i}`} className="bb-prop">
              <div className="player">
                <div className="name">{p.player_name}</div>
                <div className="meta">
                  {p.team_code} · <span className="side">{p.side}</span> {fmtLine(p.line)} {p.market_code}
                </div>
              </div>
              <div className="odds-cell">{fmtOdds(p.book_odds)}</div>
              <div className="edge-cell">{fmtPct(p.edge_pct)}</div>
              <div className="signal-tag">{p.signal ?? "—"}</div>
              <div className="stale">⏳ {p.status ?? "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Overtime Trap ───────────────────────────────────────────────
function OvertimeTrapTab(): JSX.Element {
  const { loading, error, data } = useOvertimeTrap();
  if (loading) return <Loading label="Loading overtime signals…" />;
  const populated = !!data && data.signal_count > 0;

  if (!populated) {
    return (
      <>
        {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load overtime traps" body={error} ghosts={4} /> : null}
        <AwaitPanel
          title="No overtime traps today"
          body="Yes-Overtime signals require a clean gap between our model probability and the book's implied probability. Triggers fire 90 min before tip."
          icon="clock"
        />
      </>
    );
  }
  return (
    <>
      <div className="bb-section-title">
        <h3>Overtime Trap</h3>
        <div className="sub">{data!.signal_count} candidates · STRIKZA OT prob vs book implied</div>
      </div>
      <div className="bb-ot-grid">
        {data!.signals.map((t, i) => {
          const usPct = t.bfpe_ot_prob * 100;
          const bookPct = t.book_implied_prob * 100;
          const gapPct = t.gap_pct * 100;
          return (
            <div key={`${t.game_id}-${i}`} className="bb-ot">
              <div className="bb-ot-head">
                <div className="matchup">{t.matchup}</div>
                <div className={`conf ${t.confidence === "HIGH" ? "high" : "med"}`}>{t.confidence} confidence</div>
              </div>
              <OtArc us={usPct} book={bookPct} gap={gapPct} />
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border-faint)", display: "flex", justifyContent: "space-between", alignItems: "baseline", fontFamily: "var(--font-mono)", fontSize: 11.5 }}>
                <span style={{ color: "var(--text-tertiary)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Recommended</span>
                <span style={{ color: "var(--bb-orange-text)", fontWeight: 600 }}>{t.recommended_bet} @ {fmtOdds(t.book_odds)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

type SignalTab = "daily" | "prop" | "ot";

export function SignalsScreen(): JSX.Element {
  const [tab, setTab] = useState<SignalTab>("daily");
  const { card } = useBasketballDailyCard();
  const { data: props } = usePropPulse();
  const { data: ot } = useOvertimeTrap();

  return (
    <div className="bb" data-screen-label="signals">
      <BbHead kicker="SIGNALS" title="Basketball" />

      <div className="bb-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "daily"} className={`bb-tab ${tab === "daily" ? "active" : ""}`} onClick={() => setTab("daily")}>
          Daily Card <span className="ct">{card?.betCount ?? 0}</span>
        </button>
        <button type="button" role="tab" aria-selected={tab === "prop"} className={`bb-tab ${tab === "prop" ? "active" : ""}`} onClick={() => setTab("prop")}>
          Prop Pulse <span className="ct">{props?.count ?? 0}</span>
        </button>
        <button type="button" role="tab" aria-selected={tab === "ot"} className={`bb-tab ${tab === "ot" ? "active" : ""}`} onClick={() => setTab("ot")}>
          Overtime Trap <span className="ct">{ot?.signal_count ?? 0}</span>
        </button>
      </div>

      {tab === "daily" ? <DailyCardTab /> : tab === "prop" ? <PropPulseTab /> : <OvertimeTrapTab />}
    </div>
  );
}
